import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ENTERPRISE, tabIdToProductLevels } from './constants/routing/Tabs';
import Application from 'pricing-pages-ui/containers/Application';
import { useSelector } from 'react-redux';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import Loadable from 'UIComponents/decorators/Loadable';
import BundlePage from './components/bundle-page/BundlePage';
import CodeSplitEnvWrapper from './components/common/env/CodeSplitEnvWrapper';
import ComparePage from './components/compare/ComparePage';
import CompareProInterstitial from './components/ebx642/CompareProInterstitial';
import ErrorPage from './components/error-page/ErrorPage';
import FreeToolsPage from './components/free-tools-page/FreeToolsPage';
import ProductPage from './components/product-pages/ProductPage';
import SplashPage from './components/splash-page/SplashPage';
import SuitePage from './components/suite-page/SuitePage';
import SuiteStarterPage from './components/suite-starter-page/SuiteStarterPage';
import { isAppAuthSelector } from './selectors/AuthSelectors'; // @ts-expect-error Not migrated to TS

import ConstantsTest from './components/tests/ConstantsTest';
import { useIsMobile } from './context/IsMobileContext';
import { useGetAllProductsByProductLine } from './hooks/useGetAllProductsByProductLine';
import MobileRoutes from './MobileRoutes';
import { isUngatedForSeatsSelector } from './selectors/GatingSelectors';

const ProductPageRoute = ({
  path,
  Component = ProductPage,
  productLine
}) => {
  const isAppAuth = useSelector(isAppAuthSelector);
  useGetAllProductsByProductLine({});

  if (isAppAuth) {
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      render: () => /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      })
    });
  }

  return /*#__PURE__*/_jsx(Route, {
    path: `${path}/:tab?`,
    render: ({
      match: {
        params: {
          tab
        }
      }
    }) => {
      if (!tab || !Object.keys(tabIdToProductLevels).includes(tab)) {
        getUsageTracker().setProperties({
          defaultedTab: ENTERPRISE
        });
        return /*#__PURE__*/_jsx(Redirect, {
          to: createToObject(`${path}/${ENTERPRISE}`)
        });
      }

      return /*#__PURE__*/_jsx(Component, {
        productLine: productLine
      });
    }
  });
};

const ContactsConfirmationPage = Loadable({
  loader: () => import(
  /* webpackChunkName: "contacts-confirmation" */
  'pricing-pages-ui/components/contacts-confirmation-page/ContactsConfirmationPage'),
  LoadingComponent: () => null
});
const InterstitialPage = Loadable({
  loader: () => import(
  /* webpackChunkName: "contacts-confirmation" */
  'pricing-pages-ui/components/interstitial/InterstitialPage'),
  LoadingComponent: () => null
});
export default function Routes() {
  // TODO: Wrap with Public application component HOC
  const isAppAuth = useSelector(isAppAuthSelector);
  const isSeatsPilot = useSelector(isUngatedForSeatsSelector);
  getUsageTracker().setProperties({
    isSeatsPilot
  });
  const isMobile = useIsMobile();

  const renderDesktopRoutes = () => {
    return /*#__PURE__*/_jsx(Application, {
      children: /*#__PURE__*/_jsxs(Switch, {
        children: [isAppAuth && /*#__PURE__*/_jsx(Route, {
          path: "/:page/*",
          render: ({
            location,
            match: {
              params: {
                page
              }
            }
          }) => {
            return /*#__PURE__*/_jsx(Redirect, {
              to: Object.assign({}, location, {
                pathname: `/${page}`
              })
            });
          }
        }), /*#__PURE__*/_jsx(Route, {
          path: "/confirm-contacts",
          render: ({
            location: {
              state
            },
            history
          }) => {
            if (!state) {
              history.goBack();
              return null;
            }

            return /*#__PURE__*/_jsx(ContactsConfirmationPage, {});
          }
        }), /*#__PURE__*/_jsx(Route, {
          path: "/interstitial",
          render: () => {
            return /*#__PURE__*/_jsx(InterstitialPage, {});
          }
        }), /*#__PURE__*/_jsx(Route, {
          path: "/bundle",
          component: BundlePage
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/suite",
          Component: SuitePage
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/splash",
          Component: SplashPage
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/sales",
          productLine: SALES
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/service",
          productLine: SERVICE
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/cms",
          productLine: CMS
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/marketing",
          productLine: MARKETING
        }), /*#__PURE__*/_jsx(ProductPageRoute, {
          path: "/operations",
          productLine: OPERATIONS
        }), /*#__PURE__*/_jsx(Route, {
          path: "/crm",
          component: FreeToolsPage
        }), /*#__PURE__*/_jsx(Route, {
          path: "/compare",
          component: ComparePage
        }), /*#__PURE__*/_jsx(Route, {
          path: "/suite-starter",
          component: SuiteStarterPage
        }), /*#__PURE__*/_jsx(Route, {
          path: "/legacy-starter-confirmation",
          children: /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            authedComponentLoader: () => import(
            /* webpackChunkName: "legacy-starter-sidegrade" */
            `pricing-pages-ui/components/legacy-starter-sidegrade-page/SidegradeWarningPage`),
            publicComponentLoader: () => import(
            /* webpackChunkName: "error-page" */
            `pricing-pages-ui/components/error-page/ErrorPage`)
          })
        }), /*#__PURE__*/_jsx(Route, {
          path: "/compare-professional",
          component: CompareProInterstitial
        }), isAppAuth ? /*#__PURE__*/_jsx(Redirect, {
          from: "/",
          exact: true,
          to: createToObject('/marketing')
        }) : /*#__PURE__*/_jsx(Redirect, {
          from: "/",
          exact: true,
          to: createToObject('/splash')
        }), /*#__PURE__*/_jsx(Route, {
          path: "/_constants-test",
          children: /*#__PURE__*/_jsx(ConstantsTest, {})
        }), /*#__PURE__*/_jsx(Route, {
          component: ErrorPage
        })]
      })
    });
  };

  const renderMobileRoutes = () => {
    return /*#__PURE__*/_jsx(MobileRoutes, {});
  };

  if (isMobile) {
    return renderMobileRoutes();
  }

  return renderDesktopRoutes();
}